import "ag-grid-enterprise";
import { Badge, Box, Group, Text } from "@mantine/core";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { skipToken } from "@reduxjs/toolkit/query";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { useMemo } from "react";
import {
  openapi,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiResponse,
} from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DrofusDisciplineBadge } from "../components/DrofusDisciplineBadge";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import { DrofusForekomstDetailsModal } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import {
  closeDrofusEndringerOccurrenceDetails,
  openDrofusEndringerOccurrenceDetails,
  selectDrofusEndringerRoomGroupsApiArg,
  selectDrofusEndringerRoomGroupsSummary,
  selectDrofusEndringerSelectedOccurrenceRef,
} from "./drofusEndringerSlice";

type DrofusRoomGroupRow =
  PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiResponse["items"][number];
type DrofusEndringDetailRow = DrofusRoomGroupRow["changes"][number];

const changeTypeLabel: Record<DrofusEndringDetailRow["changeType"], string> = {
  updated: "Endret",
  added: "Ny",
  removed: "Fjernet",
};

const changeTypeColor: Record<DrofusEndringDetailRow["changeType"], string> = {
  updated: "yellow",
  added: "green",
  removed: "red",
};

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

function getOccurrenceId(row?: DrofusEndringDetailRow): number | null {
  if (!row || row.entityType !== "occurrence") return null;
  if (typeof row.entityId === "number") return row.entityId;

  const numericId = Number(row.entityId);
  return Number.isInteger(numericId) ? numericId : null;
}

const ChangeTypeRenderer: React.FC<
  ICellRendererParams<DrofusEndringDetailRow, DrofusEndringDetailRow["changeType"]>
> = ({ value }) => {
  if (!value) return null;
  return (
    <Badge size="sm" radius="sm" color={changeTypeColor[value]} variant="light">
      {changeTypeLabel[value]}
    </Badge>
  );
};

const DisciplinesRenderer: React.FC<ICellRendererParams<unknown, string[]>> = ({ value }) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <DrofusDisciplineBadge key={discipline} discipline={discipline} />
      ))}
    </Group>
  );
};

const CountSummaryRenderer: React.FC<ICellRendererParams<DrofusRoomGroupRow>> = ({ data }) => {
  if (!data) return null;
  return (
    <Group gap={6} wrap="nowrap">
      <Badge size="sm" radius="sm" variant="light" color="gray">
        {data.changeCount}
      </Badge>
      {data.updatedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="yellow">
          E {data.updatedCount}
        </Badge>
      )}
      {data.addedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="green">
          N {data.addedCount}
        </Badge>
      )}
      {data.removedCount > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="red">
          F {data.removedCount}
        </Badge>
      )}
    </Group>
  );
};

const RoomNumberRenderer: React.FC<
  ICellRendererParams<DrofusRoomGroupRow, string | null> & {
    onOpenRoom?: (roomNumber: string) => void;
  }
> = ({ value, onOpenRoom }) => {
  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenRoom?.(value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

const OccurrenceIdRenderer: React.FC<
  ICellRendererParams<DrofusEndringDetailRow, number | null> & {
    onOpenDetails?: (row: DrofusEndringDetailRow, occurrenceId: number) => void;
  }
> = ({ value, data, onOpenDetails }) => {
  if (!data || !value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenDetails?.(data, value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

const RoomChangesDetailRenderer: React.FC<{ data?: DrofusRoomGroupRow }> = ({ data }) => {
  const dispatch = useAppDispatch();
  const rowData = data?.changes ?? [];
  const columnDefs = useMemo<ColDef<DrofusEndringDetailRow>[]>(
    () => [
      {
        headerName: "Dato",
        field: "runStartedAt",
        width: 150,
        valueFormatter: ({ value }: ValueFormatterParams<DrofusEndringDetailRow, string>) =>
          formatDateTime(value),
      },
      {
        headerName: "Type",
        field: "changeType",
        width: 110,
        cellRenderer: "ChangeTypeRenderer",
      },
      {
        headerName: "Forekomst ID",
        colId: "occurrenceId",
        width: 130,
        valueGetter: ({ data }) => getOccurrenceId(data),
        cellRenderer: "OccurrenceIdRenderer",
        cellRendererParams: {
          onOpenDetails: (row: DrofusEndringDetailRow, occurrenceId: number) =>
            dispatch(
              openDrofusEndringerOccurrenceDetails({
                projectNo: row.projectNo,
                runId: row.runId,
                occurrenceId,
              })
            ),
        },
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        width: 140,
        cellRenderer: "DisciplinesRenderer",
      },
      {
        headerName: "Bygg",
        field: "roomBuilding",
        width: 110,
      },
      {
        headerName: "Etasje",
        field: "roomFloor",
        width: 130,
      },
      {
        headerName: "CompCode",
        colId: "compCode",
        width: 140,
        valueGetter: ({ data }) => data?.context.article?.compCode,
      },
      {
        headerName: "Artikkelnavn",
        colId: "articleName",
        minWidth: 240,
        flex: 1,
        valueGetter: ({ data }) => data?.context.article?.name,
      },
      {
        headerName: "Felt",
        field: "displayName",
        minWidth: 220,
        flex: 1,
        valueGetter: ({ data }) => data?.displayName ?? data?.normalizedPath ?? data?.changeKey,
      },
      {
        headerName: "Gammel verdi",
        field: "oldValue",
        minWidth: 170,
        flex: 1,
        valueFormatter: ({ value }) => formatValue(value),
      },
      {
        headerName: "Ny verdi",
        field: "newValue",
        minWidth: 170,
        flex: 1,
        valueFormatter: ({ value }) => formatValue(value),
      },
    ],
    [dispatch]
  );

  return (
    <Box h={Math.min(360, 88 + rowData.length * 38)} p="xs">
      <Box className="ag-theme-quartz" h="100%" w="100%">
        <AgGridReact<DrofusEndringDetailRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: true,
            minWidth: 110,
          }}
          rowHeight={34}
          headerHeight={36}
          suppressScrollOnNewData
          components={{
            ChangeTypeRenderer,
            DisciplinesRenderer,
            OccurrenceIdRenderer,
          }}
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen endringer i rommet</span>"
        />
      </Box>
    </Box>
  );
};

export const DrofusEndringerGroupedByRoomTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const apiArg = useAppSelector(selectDrofusEndringerRoomGroupsApiArg);
  const summary = useAppSelector(selectDrofusEndringerRoomGroupsSummary);
  const selectedOccurrenceRef = useAppSelector(selectDrofusEndringerSelectedOccurrenceRef);
  const { data, isLoading, isFetching, isError } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsByRoomQuery(
      apiArg ?? skipToken
    );

  const rowData = data?.items ?? [];
  const columnDefs = useMemo<ColDef<DrofusRoomGroupRow>[]>(
    () => [
      {
        headerName: "Romnummer",
        field: "roomFunctionNo",
        width: 150,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: "RoomNumberRenderer",
          innerRendererParams: {
            onOpenRoom: (roomNumber: string) => {
              dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_INFO));
              dispatch(setSelectedSpaceNumberInRedux(roomNumber));
            },
          },
        },
      },
      {
        headerName: "Romnavn",
        field: "roomName",
        minWidth: 220,
        flex: 1,
      },
      {
        headerName: "Bygg",
        field: "roomBuilding",
        width: 110,
      },
      {
        headerName: "Etasje",
        field: "roomFloor",
        width: 130,
      },
      {
        headerName: "Siste endring",
        field: "latestRunStartedAt",
        width: 160,
        valueFormatter: ({ value }: ValueFormatterParams<DrofusRoomGroupRow, string>) =>
          formatDateTime(value),
      },
      {
        headerName: "Endringer",
        colId: "changeSummary",
        width: 210,
        cellRenderer: "CountSummaryRenderer",
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        width: 170,
        cellRenderer: "DisciplinesRenderer",
      },
    ],
    [dispatch]
  );

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DrofusForekomstDetailsModal
        occurrenceRef={selectedOccurrenceRef}
        opened={selectedOccurrenceRef != null}
        onClose={() => dispatch(closeDrofusEndringerOccurrenceDetails())}
      />

      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text size="sm" c={isError ? "red" : "dimmed"} style={{ whiteSpace: "nowrap" }}>
          {isError
            ? "Kunne ikke laste romgrupper"
            : `${summary.firstVisibleRow}-${summary.lastVisibleRow} av ${summary.total} rom`}
        </Text>
        {isFetching && (
          <Badge variant="light" color="blue" radius="sm">
            Laster
          </Badge>
        )}
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<DrofusRoomGroupRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: true,
            minWidth: 120,
          }}
          loading={isLoading}
          animateRows
          masterDetail
          detailRowAutoHeight
          isRowMaster={(row) => Boolean(row?.changes.length)}
          detailCellRenderer={RoomChangesDetailRenderer}
          getRowId={({ data }) => data.roomFunctionNo ?? "uten-rom"}
          suppressScrollOnNewData
          tooltipShowDelay={150}
          components={{
            CountSummaryRenderer,
            DisciplinesRenderer,
            RoomNumberRenderer,
          }}
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen rom med endringer funnet</span>"
        />
      </Box>

      <Text size="sm" c="dimmed" mt="sm">
        {summary.total} rom
      </Text>
    </Box>
  );
};
